// A new document made from an index's tree: where in the tree it goes is
// screen state of the index's screen (never the URL), read when the document
// screen has made it.
//
// Promise: "a new document under X" opens /me/documents/index/<key>/new; when
// the document is made it is put under X (at the end) before it opens. Made
// from the header's "+", or after a reload of /new, it goes at the top's end.
import { useNavigate } from "@tanstack/react-router";
import type { DocIndexDTO } from "@trameria/core/doc-indexes-shared";
import type { PageDTO } from "@trameria/core/documents-shared";
import {
  createContext,
  type ReactNode,
  useContext,
  useMemo,
  useRef,
} from "react";
import { useApi } from "../../app/context";
import { type DocumentPlace, useDocumentPaths } from "../place";
import { NEW_DOCUMENT_KEY } from "./keys";

export interface NewHere {
  /** Opens the new document's screen; it will go under `parentId`. */
  start(index: DocIndexDTO, where: { parentId: string | null }): void;
}

interface Pending {
  indexKey: string;
  parentId: string | null;
}

const NewHereContext = createContext<{
  pending: { current: Pending | null };
} | null>(null);

/** Around an index's screen: keeps where the next new document goes. */
export function NewHereScope({ children }: { children: ReactNode }) {
  const pending = useRef<Pending | null>(null);
  const value = useMemo(() => ({ pending }), []);
  return (
    <NewHereContext.Provider value={value}>{children}</NewHereContext.Provider>
  );
}

export function useNewHere(): NewHere {
  const scope = useContext(NewHereContext);
  const navigate = useNavigate();
  const paths = useDocumentPaths();
  return {
    start(index, where) {
      if (scope)
        scope.pending.current = {
          indexKey: index.key,
          parentId: where.parentId,
        };
      void navigate({ to: paths.indexDocument(index.key, NEW_DOCUMENT_KEY) });
    },
  };
}

/** The document screen's place inside the index `indexKey`. */
export function useIndexPlace(
  indexKey: string,
  indexId: string | null,
): DocumentPlace {
  const api = useApi();
  const scope = useContext(NewHereContext);
  const paths = useDocumentPaths();
  return useMemo(
    () => ({
      pathOf: (key: string) => paths.indexDocument(indexKey, key),
      back: paths.index(indexKey),
      // Made where the index is (a workspace's index: its space).
      beside: indexId ?? indexKey,
      async created(page: PageDTO) {
        const pending = scope?.pending.current;
        if (scope) scope.pending.current = null;
        const target = indexId ?? indexKey;
        await api(`/api/me/doc-indexes/${encodeURIComponent(target)}/entries`, {
          method: "POST",
          body: {
            pageId: page.id,
            parentId: pending?.indexKey === indexKey ? pending.parentId : null,
          },
        });
      },
    }),
    [api, scope, paths, indexKey, indexId],
  );
}
